import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { TIMER_CHANGED_EVENT } from '../utils/timerSync';

const fetchWsAuthToken = async () => {
  try {
    const res = await axios.get(`${API_BASE_URL}ws-auth-token/`, { withCredentials: true });
    return res.data?.token || null;
  } catch {
    return null;
  }
};

const getWebSocketURL = (authToken = null) => {
  // Always use the current window location to ensure same-origin (cookies work)
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  let host = window.location.host;

  if (process.env.NODE_ENV === 'development') {
    host = `${window.location.hostname}:8000`;
  }

  const BASE_URL = process.env.REACT_APP_BASE_URL || '/';
  let path = '';

  if (BASE_URL.startsWith('http://') || BASE_URL.startsWith('https://')) {
    try {
      const url = new URL(BASE_URL);
      path = url.pathname.replace(/\/$/, '');
    } catch (e) {
      path = '';
    }
  } else {
    path = BASE_URL === '/' ? '' : BASE_URL.replace(/\/$/, '');
  }

  let wsUrl = `${protocol}//${host}${path}/ws/user-events/`;
  if (authToken) wsUrl += `?auth_token=${authToken}`;
  return wsUrl;
};

/**
 * Per-user WebSocket that calls onPermissionsUpdated() whenever the backend pushes a
 * permissions_updated event (teamspace membership, role, or timezone changed on this
 * user's TeamspaceMemberRole). Lets AuthContext silently refetch /users/me/ so new
 * access reflects immediately, without requiring logout/login or a full page reload.
 *
 * Also carries timer_changed (the floating-button timer was started/edited/stopped in some tab
 * or browser), re-fired on window as TIMER_CHANGED_EVENT for hooks/useTimerSync.js — and once
 * after every reconnect, since pushes sent while the socket was down are lost.
 */
export const usePermissionsWebSocket = (isAuthenticated, onPermissionsUpdated) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000;
  const pingIntervalRef = useRef(null);
  const hasConnectedRef = useRef(false);

  // Keep the latest callback without retriggering the connect effect
  const onUpdateRef = useRef(onPermissionsUpdated);
  onUpdateRef.current = onPermissionsUpdated;

  const connect = useCallback(async () => {
    if (!isAuthenticated) return;
    if (wsRef.current) {
      wsRef.current.close();
    }

    try {
      const authToken = await fetchWsAuthToken();
      const wsUrl = getWebSocketURL(authToken);
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        reconnectAttempts.current = 0;
        if (hasConnectedRef.current) window.dispatchEvent(new Event(TIMER_CHANGED_EVENT));
        hasConnectedRef.current = true;
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'permissions_updated' && onUpdateRef.current) {
            onUpdateRef.current(data);
          } else if (data.type === 'timer_changed') {
            window.dispatchEvent(new Event(TIMER_CHANGED_EVENT));
          }
        } catch (e) {
          // ignore malformed messages
        }
      };

      ws.onerror = () => {};

      ws.onclose = (event) => {
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }
        if (event.code !== 1000 && isAuthenticated) {
          if (reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current += 1;
            reconnectTimeoutRef.current = setTimeout(() => connect(), reconnectDelay);
          }
        }
      };

      wsRef.current = ws;
    } catch (e) {
      // Connection failed - will retry on next connect attempt
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) connect();

    return () => {
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
      if (wsRef.current) {
        wsRef.current.close(1000);
        wsRef.current = null;
      }
    };
  }, [isAuthenticated, connect]);
};
